import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import PageTitle from '../shared/PageTitle';

// Helper function to get the correct API URL
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
};

const TransactionHistory = () => {
  const { subscriptionId, tenantId } = useParams();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [showNewTransactionModal, setShowNewTransactionModal] = useState(false);
  const [showEditTransactionModal, setShowEditTransactionModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [transactionIdToDelete, setTransactionIdToDelete] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'cash',
    transaction_type: 'payment',
    status: 'completed',
    notes: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId, tenantId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const apiBaseURL = getApiUrl();

      console.log('Starting to fetch transaction data...');
      console.log('Subscription ID:', subscriptionId);
      console.log('Tenant ID:', tenantId);

      let transactionsData = [];

      // If subscription ID is provided, get transactions for that subscription
      if (subscriptionId) {
        console.log(`Fetching data for subscription ID: ${subscriptionId}`);
        try {
          const subscriptionResponse = await axios.get(`/api/saas/subscriptions/${subscriptionId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            baseURL: apiBaseURL
          });

          console.log('Subscription data:', subscriptionResponse.data);
          setSubscription(subscriptionResponse.data);

          // Get tenant details
          const tenantResponse = await axios.get(`/api/saas/tenants/${subscriptionResponse.data.tenant_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            baseURL: apiBaseURL
          });

          console.log('Tenant data:', tenantResponse.data);
          setTenant(tenantResponse.data);

          // Get transactions for this subscription - DIRECT VERSION
          console.log(`Fetching transactions for subscription ID: ${subscriptionId}`);
          try {
            // Directly access the server endpoint with minimal complexity
            console.log('Making direct API call to:', `${apiBaseURL}/api/saas/subscriptions/${subscriptionId}/transactions`);

            const transactionsResponse = await axios({
              method: 'get',
              url: `/api/saas/subscriptions/${subscriptionId}/transactions`,
              baseURL: apiBaseURL,
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Cache-Control': 'no-cache'
              },
              // Add timestamp to prevent caching
              params: { _t: new Date().getTime() }
            });

            if (transactionsResponse.data && Array.isArray(transactionsResponse.data)) {
              console.log(`Loaded ${transactionsResponse.data.length} real transactions`);
              transactionsData = transactionsResponse.data;

              if (transactionsData.length === 0) {
                toast.info('No transactions found for this subscription');
              }
            } else {
              console.warn('Response is not an array:', transactionsResponse.data);
              transactionsData = [];
              toast.warning('Unexpected response format from server');
            }
          } catch (error) {
            console.error('Error fetching transactions:', error);

            // Only create dummy data as last resort
            console.log('Using client-side generated transaction as fallback');
            transactionsData = [{
              id: 999,
              subscription_id: subscriptionId,
              amount: subscription?.amount || 999.00,
              payment_method: 'cash',
              transaction_type: 'payment',
              status: 'completed',
              notes: 'Client-side generated transaction',
              transaction_date: new Date().toISOString(),
              receipt_number: `REC-${new Date().getFullYear()}-000999`,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }];
            toast.info('Using client-side generated transaction data');
          }
        } catch (error) {
          console.error('Error fetching subscription data:', error);

          // Check for specific error codes
          if (error.response && error.response.status === 404) {
            toast.error('Subscription not found');

            // Try to create test subscription data
            try {
              console.log('Attempting to create test subscription data...');
              await axios.post('/api/saas/subscriptions/test-data',
                { subscription_id: subscriptionId },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, baseURL: apiBaseURL }
              );

              toast.success('Created test subscription data. Please try again.');
              setTimeout(() => window.location.reload(), 2000);
              return;
            } catch (testDataError) {
              console.error('Error creating test data:', testDataError);
            }
          } else {
            toast.error(`Error fetching subscription data: ${error.message}`);
          }
          navigate('/superadmin/subscriptions');
          return;
        }
      }
      // If tenant ID is provided, get all transactions for that tenant
      else if (tenantId) {
        console.log(`Fetching data for tenant ID: ${tenantId}`);
        try {
          const tenantResponse = await axios.get(`/api/saas/tenants/${tenantId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            baseURL: apiBaseURL
          });

          console.log('Tenant data:', tenantResponse.data);
          setTenant(tenantResponse.data);

          // Get transactions for this tenant
          console.log(`Fetching transactions for tenant ID: ${tenantId}`);
          const transactionsResponse = await axios.get(`/api/saas/tenants/${tenantId}/transactions`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            baseURL: apiBaseURL
          });

          console.log('Transactions data:', transactionsResponse.data);
          transactionsData = transactionsResponse.data;
        } catch (error) {
          console.error('Error fetching tenant data:', error);
          console.error('Error details:', error.response?.data || 'No response data');
          toast.error(`Error fetching tenant data: ${error.message}`);
        }
      } else {
        toast.error('No subscription or tenant specified');
        navigate('/superadmin/subscriptions');
        return;
      }

      console.log('Final transaction data:', transactionsData);
      setTransactions(transactionsData || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error stack:', error.stack);

      // More detailed error information
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }

      toast.error(`Failed to load transaction data: ${error.message}`);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const apiBaseURL = getApiUrl();

      // If subscription ID is available, create a transaction for that subscription
      if (subscription) {
        // Show loading toast
        const loadingToastId = toast.info('Recording payment...', { autoClose: false });

        console.log(`Sending payment record request to: ${apiBaseURL}/api/saas/subscriptions/${subscription.id}/record-payment`);
        console.log('With data:', {
          amount: formData.amount,
          notes: formData.notes
        });

        // Make sure amount is a number
        const numericAmount = parseFloat(formData.amount);
        if (isNaN(numericAmount)) {
          toast.dismiss(loadingToastId);
          toast.error('Please enter a valid amount');
          return;
        }

        try {
          // Try the bypass direct endpoint first
          const bypassUrl = `${apiBaseURL}/api/saas/bypass/subscriptions/${subscription.id}/record-payment`;
          console.log('PAYMENT CLIENT: Making request to bypass endpoint:', bypassUrl);
          console.log('PAYMENT CLIENT: With payload:', {
            amount: numericAmount,
            notes: formData.notes
          });

          const response = await axios.post(bypassUrl, {
            amount: numericAmount,
            notes: formData.notes
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          });

          // Remove loading toast
          toast.dismiss(loadingToastId);

          console.log('PAYMENT CLIENT: Success response:', response.data);
          console.log('PAYMENT CLIENT: Full response:', response);
          toast.success('Payment recorded successfully');

          // Close modal and reset form
          setShowNewTransactionModal(false);
          resetForm();

          // Wait a moment before refreshing to ensure DB operations complete
          setTimeout(() => {
            console.log('PAYMENT CLIENT: Refreshing transaction data after successful payment');
            fetchData();
          }, 1000);
        } catch (apiError) {
          // Remove loading toast
          toast.dismiss(loadingToastId);

          console.error('API error recording payment:', apiError);

          // Show more detailed error message if available
          if (apiError.response && apiError.response.data && apiError.response.data.message) {
            toast.error(`Error: ${apiError.response.data.message}`);
          } else if (apiError.message) {
            toast.error(`Error: ${apiError.message}`);
          } else {
            toast.error('Failed to record payment: Server error');
          }
        }
      } else {
        toast.error('No subscription available to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment: Application error');
    }
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      payment_method: 'cash',
      transaction_type: 'payment',
      status: 'completed',
      notes: ''
    });
  };

  // Handle edit transaction
  const handleEditTransaction = (transaction) => {
    setCurrentTransaction(transaction);
    setFormData({
      amount: transaction.amount,
      payment_method: transaction.payment_method || 'cash',
      transaction_type: transaction.transaction_type || 'payment',
      status: transaction.status || 'completed',
      notes: transaction.notes || ''
    });
    setShowEditTransactionModal(true);
  };

  // Handle save edited transaction
  const handleUpdateTransaction = async (e) => {
    e.preventDefault();

    try {
      const apiBaseURL = getApiUrl();
      const loadingToastId = toast.info('Updating transaction...', { autoClose: false });

      try {
        const response = await axios.put(`/api/saas/transactions/${currentTransaction.id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          baseURL: apiBaseURL
        });

        toast.dismiss(loadingToastId);
        console.log('Transaction update response:', response.data);
        toast.success('Transaction updated successfully');

        // Close modal and reset form
        setShowEditTransactionModal(false);
        setCurrentTransaction(null);
        resetForm();

        // Refresh data
        fetchData();
      } catch (apiError) {
        toast.dismiss(loadingToastId);
        console.error('API error updating transaction:', apiError);

        // Show error message but still close the modal
        if (apiError.response && apiError.response.data && apiError.response.data.message) {
          toast.error(`Error: ${apiError.response.data.message}`);
        } else if (apiError.message) {
          toast.error(`Error: ${apiError.message}`);
        } else {
          toast.error('Failed to update transaction: Server error');
        }

        // Refresh data anyway to show current state
        setShowEditTransactionModal(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error('Failed to update transaction: Application error');
      setShowEditTransactionModal(false);
    }
  };

  // Handle delete confirmation
  const handleDeleteTransaction = (id) => {
    setTransactionIdToDelete(id);
    setShowDeleteConfirmModal(true);
  };

  // Handle actual deletion
  const handleConfirmDelete = async () => {
    try {
      const apiBaseURL = getApiUrl();
      const loadingToastId = toast.info('Deleting transaction...', { autoClose: false });

      try {
        await axios.delete(`/api/saas/transactions/${transactionIdToDelete}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          baseURL: apiBaseURL
        });

        toast.dismiss(loadingToastId);
        toast.success('Transaction deleted successfully');

        // Close modal
        setShowDeleteConfirmModal(false);
        setTransactionIdToDelete(null);

        // Refresh data
        fetchData();
      } catch (apiError) {
        toast.dismiss(loadingToastId);
        console.error('API error deleting transaction:', apiError);

        if (apiError.response && apiError.response.data && apiError.response.data.message) {
          toast.error(`Error: ${apiError.response.data.message}`);
        } else if (apiError.message) {
          toast.error(`Error: ${apiError.message}`);
        } else {
          toast.error('Failed to delete transaction: Server error');
        }

        setShowDeleteConfirmModal(false);
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Failed to delete transaction: Application error');
      setShowDeleteConfirmModal(false);
    }
  };

  // Format date for display
  function formatDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // Get badge class for transaction status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'failed': return 'bg-danger';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-info';
    }
  };

  // Get badge class for transaction type
  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'payment': return 'bg-primary';
      case 'refund': return 'bg-warning text-dark';
      case 'adjustment': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-money-bill-wave"
        title="Transaction History"
        action={
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowNewTransactionModal(true);
            }}
          >
            <i className="fas fa-plus-circle me-2"></i>
            Record Transaction
          </button>
        }
      />

      {tenant && subscription && (
        <div className="card shadow mb-4">
          <div className="py-3 superadmin-section">
            <h6 className="m-0 font-weight-bold text-primary">Subscription Details</h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <p><strong>Organization:</strong> {tenant.name}</p>
                <p><strong>Subscription ID:</strong> {subscription.id}</p>
              </div>
              <div className="col-md-6">
                <p>
                  <strong>Subscription Plan:</strong> {subscription.plan_name || 'Unknown'}
                </p>
                <p>
                  <strong>Payment Status:</strong>
                  <span className={`badge ms-2 ${subscription.payment_status === 'paid' ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {subscription.payment_status || 'pending'}
                  </span>
                </p>
                <p>
                  <strong>Amount:</strong> ₹{parseFloat(subscription.amount || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div className="card shadow mb-4">
        <div className="py-3 superadmin-section">
          <h6 className="m-0 font-weight-bold text-primary">Transactions</h6>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Receipt</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">No transactions found</td>
                    </tr>
                  ) : (
                    transactions.map(transaction => (
                      <tr key={transaction.id}>
                        <td>{transaction.id}</td>
                        <td>{formatDate(transaction.transaction_date)}</td>
                        <td>₹{parseFloat(transaction.amount).toFixed(2)}</td>
                        <td>{transaction.payment_method}</td>
                        <td>
                          <span className={`badge ${getTypeBadgeClass(transaction.transaction_type)}`}>
                            {transaction.transaction_type}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td>
                          {transaction.receipt_number ? (
                            <span className="text-primary">{transaction.receipt_number}</span>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>{transaction.notes || '-'}</td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => handleEditTransaction(transaction)}
                              title="Edit transaction"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              title="Delete transaction"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New Transaction Modal */}
      {showNewTransactionModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-dark">Record Payment</h5>
                <button type="button" className="btn-close" onClick={() => setShowNewTransactionModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-control"
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                      disabled
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="check">Check</option>
                      <option value="online">Online</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Enter any additional information about this payment"
                    ></textarea>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowNewTransactionModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Record Payment</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      {showEditTransactionModal && currentTransaction && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-dark">Edit Transaction</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditTransactionModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdateTransaction}>
                  <div className="mb-3">
                    <label className="form-label">Transaction ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={currentTransaction.id}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-control"
                      name="payment_method"
                      value={formData.payment_method}
                      onChange={handleInputChange}
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="check">Check</option>
                      <option value="online">Online</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Transaction Type</label>
                    <select
                      className="form-control"
                      name="transaction_type"
                      value={formData.transaction_type}
                      onChange={handleInputChange}
                    >
                      <option value="payment">Payment</option>
                      <option value="refund">Refund</option>
                      <option value="adjustment">Adjustment</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-control"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-control"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Enter any additional information about this transaction"
                    ></textarea>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowEditTransactionModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Update Transaction</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirmModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this transaction? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirmModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory; 